import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import HTMLFlipBook from 'react-pageflip';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, BookOpen, Volume2, VolumeX } from 'lucide-react';
import { Button } from "../components/ui/button";
import { useDispatch, useSelector } from 'react-redux';
import { fetchCatalog } from '../rtk/slices/catalog-slice';
import flipSoundSrc from '../assets/page-flip2.mp3';

// مكون الصفحة الواحدة للكتالوج
const CatalogPage = React.forwardRef(
    ({ children, className = '', pageNumber, isCover = false, isBackCover = false }, ref) => {
        let pageClasses = `page-paper overflow-hidden flex justify-center items-center shadow-lg`;
        if (isCover) {
            pageClasses += ` ${className} cover-styles`;
        } else {
            pageClasses += ` ${className} bg-slate-50`;
        }
        if (isBackCover) {
            pageClasses += ` back-cover-styles`;
        }

        return (
            <div className={pageClasses} ref={ref}>
                <div className="page-content-inner w-full h-full" style={{ background: '#f1efef' }}>
                    {children}
                </div>
            </div>
        );
    }
);
CatalogPage.displayName = 'CatalogPage';

// المكون الرئيسي لعرض الكتالوج
const ImagePageFlipCatalog = () => {
    const { t, i18n } = useTranslation();
    const flipBookRef = useRef(null);
    const wrapperRef = useRef(null);
    const audioRef = useRef(null);

    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [bookWidth, setBookWidth] = useState(0);
    const [bookHeight, setBookHeight] = useState(0);
    const [isBookReady, setIsBookReady] = useState(false); // حالة جاهزية الكتاب للتقليب والتحكم
    const [isMuted, setIsMuted] = useState(false);

    const dispatch = useDispatch();
    const { catalog: catalogImagesData, status, error } = useSelector((state) => state.catalog);

    // useEffect لجلب بيانات الكتالوج عند تحميل المكون لأول مرة
    useEffect(() => {
        // إذا كان الـ status في حالة 'idle' (لم يبدأ الجلب بعد)، ابدأ الجلب
        if (status === 'idle') {
            dispatch(fetchCatalog());
        }
    }, [dispatch, status]); // التابعيات: dispatch و status لضمان الجلب مرة واحدة

    // دالة لحساب أبعاد الكتاب بناءً على حجم الـ wrapper
    const calculateDimensions = () => {
        if (wrapperRef.current) {
            const parentEl = wrapperRef.current;
            const availableWidth = parentEl.offsetWidth;

            if (availableWidth > 0) {
                const singlePageAspectRatio = 4 / 3;
                const bookAspectRatio = (4 * 2) / 3;

                let newBookWidth = availableWidth;
                let newBookHeight = newBookWidth / bookAspectRatio;

                const maxHeight = window.innerHeight * 0.80;
                if (newBookHeight > maxHeight) {
                    newBookHeight = maxHeight;
                    newBookWidth = newBookHeight * bookAspectRatio;
                }

                // ضمان أن الأبعاد لا تقل عن حد معين وأنها أرقام صحيحة وموجبة
                const finalWidth = Math.max(320, Math.floor(newBookWidth));
                const finalHeight = Math.max(240, Math.floor(newBookHeight));

                // تحديث الـ state فقط إذا تغيرت الأبعاد بشكل ملحوظ
                if (bookWidth !== finalWidth || bookHeight !== finalHeight) {
                    setBookWidth(finalWidth);
                    setBookHeight(finalHeight);
                }
                return true; // تم الحساب بنجاح
            }
        }
        return false; // فشل الحساب
    };

    // useLayoutEffect لحساب الأبعاد وتحديثها عند تغيير حجم النافذة
    useLayoutEffect(() => {
        let requestId;
        const attemptCalculation = () => {
            const success = calculateDimensions();
            if (!success) {
                // إذا لم يتم الحساب بنجاح، حاول مرة أخرى في الإطار التالي
                requestId = requestAnimationFrame(attemptCalculation);
            }
        };

        attemptCalculation(); // ابدأ المحاولة الأولى للحساب

        const handleResize = () => {
            calculateDimensions(); // أعد الحساب عند تغيير حجم النافذة
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (requestId) {
                cancelAnimationFrame(requestId); // إلغاء أي requestAnimationFrame معلق
            }
        };
    }, []); // يعتمد فقط على الرفوف عند التركيب لأول مرة

    // useEffect لتهيئة مكتبة react-pageflip وتحديث isBookReady
    useEffect(() => {
        // تأكد أن المرجع موجود وأن البيانات متوفرة
        if (flipBookRef.current && catalogImagesData && catalogImagesData.length > 0) {
            const initializeBook = () => {
                try {
                    const pageFlipInstance = flipBookRef.current.pageFlip();
                    if (pageFlipInstance && typeof pageFlipInstance.getPageCount === 'function') {
                        const count = pageFlipInstance.getPageCount();
                        setTotalPages(count);
                        // تم التهيئة بنجاح، الكتاب جاهز
                        setIsBookReady(true);
                        // ارجع إلى الصفحة الأولى إذا لم تكن كذلك
                        if (pageFlipInstance.getCurrentPageIndex() !== 0) {
                            pageFlipInstance.turnToPage(0);
                            setCurrentPage(0);
                        }
                    } else {
                        // إذا كانت pageFlipInstance غير صالحة، أبقِ isBookReady على false
                        setIsBookReady(false);
                        console.warn("pageFlip instance not ready or getPageCount not a function.");
                    }
                } catch (error) {
                    console.error('Error initializing page-flip instance:', error);
                    setIsBookReady(false); // خطأ في التهيئة
                }
            };

            // تأخير بسيط لضمان أن DOM قد تم تحديثه وأن HTMLFlipBook قد تم تركيبه تمامًا
            const timer = setTimeout(initializeBook, 200); // يمكن زيادة هذا التأخير إذا لزم الأمر
            return () => clearTimeout(timer);
        } else {
            // إذا لم تتوفر الشروط، تأكد أن isBookReady هي false
            // وهذا سيجعل "Preparing the catalog..." تظهر
            setIsBookReady(false);
        }
    }, [catalogImagesData, bookWidth, bookHeight]); // التابعيات: بيانات الكتالوج والأبعاد

    // تشغيل صوت التقليب
    const playFlipSound = () => {
        if (audioRef.current && !isMuted) {
            audioRef.current.currentTime = 0;
            audioRef.current.play().catch(error => console.warn("Error playing sound:", error));
        }
    };

    // معالجة حدث تقليب الصفحة
    const handleFlip = (e) => {
        setCurrentPage(e.data);
        playFlipSound();
    };

    // التنقل للصفحة التالية
    const handleNextPage = () => {
        flipBookRef.current?.pageFlip()?.flipNext();
    };

    // التنقل للصفحة السابقة
    const handlePrevPage = () => {
        flipBookRef.current?.pageFlip()?.flipPrev();
    };

    // تبديل حالة كتم الصوت
    const toggleMute = () => {
        setIsMuted(!isMuted);
        if (audioRef.current) {
            audioRef.current.muted = !isMuted;
        }
    };

    // عرض رقم الصفحة للمستخدم (مع مراعاة اللغة)
    const displayUserPage = i18n.language === 'ar'
        ? `${currentPage === 0 ? '0' : currentPage} - ${currentPage + 1}`
        : `${currentPage === 0 ? '0' : currentPage} - ${currentPage + 1}`;

    // -----------------------------------------------------
    // منطق عرض حالات التحميل، الأخطاء، أو عدم وجود بيانات
    // -----------------------------------------------------

    // حالة التحميل الأولية أو انتظار جلب البيانات
    if (status === 'idle' || status === 'loading') {
        return (
            <section id="image-catalog-section" className="py-12 md:py-20 flex justify-center items-center h-screen">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-amber-500"></div>
                <p className="ml-4 text-gray-600">{t('catalog.loading', 'Loading catalog...')}</p>
            </section>
        );
    }

    // حالة وجود خطأ في جلب البيانات
    if (status === 'failed' && error) {
        return (
            <section id="image-catalog-section" className="py-12 md:py-20">
                <div className="container mx-auto px-4 text-center text-red-500">
                    <p className="error-api">{error}</p>
                </div>
            </section>
        );
    }

    // حالة نجاح الجلب ولكن لا توجد صور في الكتالوج
    if (catalogImagesData && catalogImagesData.length === 0) {
        return (
            <section id="image-catalog-section" className="py-12 md:py-20">
                <div className="container mx-auto px-4 text-center text-gray-500">
                    {t('catalog.noImages', 'No catalog images to display.')}
                </div>
            </section>
        );
    }

    // -----------------------------------------------------
    // عرض الكتالوج عندما يكون جاهزاً للعرض (الأبعاد والبيانات متوفرة)
    // -----------------------------------------------------

    return (
        <section id="catalog" className="relative py-10 md:py-20 bg-gradient-to-br from-slate-100 to-gray-200 overflow-hidden">
            <audio ref={audioRef} src={flipSoundSrc} preload="auto" />
            <div className="container mx-auto px-4">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-8 md:mb-10"
                >
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-2 flex items-center justify-center">
                        <BookOpen className="w-8 h-8 md:w-10 md:h-10 mr-3 text-amber-600" />
                        {t('nav.catalog', 'Our Catalog')}
                    </h2>
                    <p className="text-md md:text-lg text-gray-600 max-w-xl mx-auto">
                        {t('catalog.imageCatalogSubtitle', 'Flip through our latest designs and inspirations.')}
                    </p>
                </motion.div>

                <div
                    ref={wrapperRef}
                    className="catalog-image-pageflip-wrapper mx-auto flex flex-col justify-center items-center"
                    style={{
                        // تأمين حد أدنى للارتفاع حتى لو لم يتم حساب الأبعاد بعد
                        minHeight: `${bookHeight > 0 ? bookHeight + 100 : 340}px`,
                    }}
                >
                    {/* عرض HTMLFlipBook إذا كانت الأبعاد موجبة والبيانات موجودة.
                        isBookReady ستتحكم في visibility بعد التهيئة الناجحة. */}
                    {bookWidth > 0 && bookHeight > 0 && catalogImagesData.length > 0 ? (
                        <div
                            className="relative"
                            // التحكم في الرؤية باستخدام isBookReady
                            style={{
                                width: bookWidth,
                                height: bookHeight,
                                visibility: isBookReady ? 'visible' : 'hidden',
                            }}
                        >
                            <HTMLFlipBook
                                width={window.innerWidth < 750 ? 350 : bookWidth / 2} // عرض الصفحة الواحدة
                                height={window.innerWidth < 750 ? '210' : bookHeight}
                                minWidth={150}
                                maxWidth={bookWidth / 2}
                                minHeight={200}
                                maxHeight={bookHeight}
                                size="fixed"
                                maxShadowOpacity={.8}
                                showCover={false}
                                mobileScrollSupport={true}
                                onFlip={handleFlip}
                                ref={flipBookRef}
                                className="demo-image-book"
                                style={{ margin: 'auto' }}
                                startPage={0}
                                flippingTime={800}
                                usePortrait={true}
                                drawShadow={true}
                                showPageCorners={true}
                            >
                                {catalogImagesData.map((image, index) => (
                                    <CatalogPage
                                        key={`catalog-img-${index}`}
                                        pageNumber={index}
                                    >
                                        <img
                                            src={`${process.env.REACT_APP_PROJECTS_API}assest/imgs/${image.img}`}
                                            alt={`Catalog Page ${index + 1}`}
                                            className="max-w-full max-h-full object-contain"
                                        />
                                    </CatalogPage>
                                ))}
                            </HTMLFlipBook>
                        </div>
                    ) : (
                        // رسالة "Preparing the catalog..." أثناء انتظار الأبعاد والبيانات
                        <div className="flex justify-center items-center h-full w-full">
                            <p className="text-gray-500">{t('catalog.preparingBook', 'Preparing the catalog...')}</p>
                        </div>
                    )}


                    {/* أزرار التحكم والترقيم - تظهر فقط عندما يكون الكتاب جاهزًا للتقليب */}
                    {/* أزرار التحكم والترقيم - نسخة متجاوبة */}
                    {isBookReady && totalPages > 0 && (
                        <div className="flex flex-row flex-wrap justify-center items-center mt-6 md:mt-8 gap-2 sm:gap-3 w-full max-w-md mx-auto">

                            {/* زر الصفحة السابقة */}
                            <Button
                                onClick={handlePrevPage}
                                variant="outline"
                                size="sm" // استخدام حجم أصغر للزر
                                className="border-amber-500 text-amber-700 hover:bg-amber-100 hover:text-amber-800 disabled:opacity-50 shadow-sm px-3"
                                disabled={currentPage === 0}
                                aria-label={t('catalog.prevPage', 'Previous Page')}
                            >
                                {i18n.language === 'ar' ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
                                {/* إخفاء النص على الشاشات الصغيرة (أقل من sm) */}
                                <span className="hidden sm:inline mx-2">{t('catalog.prevPage', 'Previous')}</span>
                            </Button>

                            {/* ترقيم الصفحات */}
                            <span className="text-gray-700 font-medium tabular-nums text-xs sm:text-sm px-3 py-1.5 bg-white/60 rounded-md shadow-sm whitespace-nowrap">
                                {t('catalog.page', 'Page')} {displayUserPage} / {totalPages - 1}
                            </span>

                            {/* زر الصفحة التالية */}
                            <Button
                                onClick={handleNextPage}
                                variant="outline"
                                size="sm" // استخدام حجم أصغر للزر
                                className="border-amber-500 text-amber-700 hover:bg-amber-100 hover:text-amber-800 disabled:opacity-50 shadow-sm px-3"
                                disabled={currentPage >= totalPages - 2}
                                aria-label={t('catalog.nextPage', 'Next Page')}
                            >
                                {/* إخفاء النص على الشاشات الصغيرة (أقل من sm) */}
                                <span className="hidden sm:inline mx-2">{t('catalog.nextPage', 'Next')}</span>
                                {i18n.language === 'ar' ? <ChevronLeft className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                            </Button>

                            {/* زر التحكم بالصوت */}
                            <Button
                                onClick={toggleMute}
                                variant="outline"
                                size="icon"
                                className="border-amber-500 text-amber-700 hover:bg-amber-100 hover:text-amber-800 shadow-sm h-9 w-9" // تحديد حجم ثابت لأيقونة الصوت
                                aria-label={isMuted ? t('catalog.unmute', "Unmute") : t('catalog.mute', "Mute")}
                            >
                                {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </section >
    );
};

export default ImagePageFlipCatalog;